import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * On product page, it also updates the display when quantity changes (shows unit x qty and total).
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    this.#updateQuantityPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
  }

  #onQuantityUpdate = (/** @type {CustomEvent<{ quantity: number; cartLine?: number }>} */ event) => {
    if (event.detail?.cartLine != null) return;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection?.contains(/** @type {Node} */ (event.target))) return;
    this.#updateQuantityPrice(event.detail?.quantity);
  };

  #onVariantUpdate = (/** @type {VariantUpdateEvent} */ event) => {
    this.updatePrice(event);
    this.#updateQuantityPrice();
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };

  /**
   * Updates price display based on quantity (product page only).
   * When qty > 1: shows "unit x qty" above and total below.
   * @param {number} [quantity] - Override quantity (from event). If not provided, reads from quantity input.
   */
  #updateQuantityPrice(quantity) {
    const container = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="priceContainer"][data-quantity-aware="true"]')
    );
    if (!container) return;

    const unitPriceCents = Number(container.dataset.unitPriceCents);
    const unitPriceFormatted = container.dataset.unitPriceFormatted ?? '';
    const moneyFormat = this.dataset.moneyFormat ?? '{{amount}}';
    const currency = this.dataset.currency ?? 'USD';

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.closest('.shopify-section, dialog')?.querySelector('input[name="quantity"]')
    );
    const qty = quantity ?? Number(quantityInput?.value ?? 1);

    const unitQuantityLine = /** @type {HTMLElement | null} */ (
      container.querySelector('[ref="unitQuantityLine"]')
    );
    const unitQuantityText = container.querySelector('[ref="unitQuantityText"]');
    const mainPrice = container.querySelector('[ref="mainPrice"]');

    if (!unitQuantityLine || !unitQuantityText || !mainPrice) return;

    if (qty > 1) {
      unitQuantityLine.hidden = false;
      unitQuantityText.textContent = `${unitPriceFormatted} × ${qty}`;
      const totalCents = unitPriceCents * qty;
      // Use amount_no_decimals for whole numbers (e.g. $90 instead of $90.00)
      const format =
        totalCents % 100 === 0
          ? moneyFormat.replace(/\{\{\s*amount\s*\}\}/g, '{{amount_no_decimals}}')
          : moneyFormat;
      mainPrice.textContent = formatMoney(totalCents, format, currency);
    } else {
      unitQuantityLine.hidden = true;
      mainPrice.textContent = unitPriceFormatted || '\u00A0';
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
